import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/layout/PageHeader";
import { Timeline } from "@/components/timeline/Timeline";
import { MeasurementSilhouette } from "@/components/measurements/MeasurementSilhouette";
import { QuoteBuilder } from "@/components/workshop/QuoteBuilder";
import { QuotePreview } from "@/components/workshop/QuotePreview";
import { DispatchDialog } from "@/components/workshop/DispatchDialog";
import { Money } from "@/components/common/Money";
import { toast } from "sonner";
import {
  ArrowLeft,
  Package,
  User,
  Phone,
  MapPin,
  ExternalLink,
  Shirt,
  Calendar,
  Ruler,
  FileText,
  Truck,
  Clock,
  Play,
  CheckCircle,
  Image,
} from "lucide-react";

interface OrderItem {
  id: string;
  garmentType: string;
  qty: number;
  tagCode?: string;
  conditionNotes?: string;
  intakePhotos?: string[];
  intakeNotes?: string;
  intakeConfirmedAt?: string;
  status: string;
}

interface OrderData {
  id: string;
  publicId: string;
  orderType: string;
  status: string;
  cutoffEligible: boolean;
  promisedDeliveryAt?: string;
  customer: {
    id: string;
    publicId: string;
    name: string;
    phone?: string;
    email?: string;
  };
  address: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    pincode: string;
    lat?: number;
    lng?: number;
  };
  area: {
    id: string;
    name: string;
    deliveryFee: number;
  };
  items: OrderItem[];
  measurements?: {
    id: string;
    versionNo: number;
    bustCm: number;
    waistCm: number;
    hipCm: number;
    shoulderCm: number;
    sleeveLengthCm: number;
    dressLengthCm: number;
    notes?: string;
  };
  activeQuote?: {
    id: string;
    versionNo: number;
    status: string;
    subtotal: number;
    tax: number;
    deliveryFee: number;
    total: number;
    expiresAt?: string;
    lineItems: any[];
    createdBy?: { name: string };
    createdAt: string;
  };
  quoteHistory: { id: string; versionNo: number; status: string; total: number; createdAt: string }[];
  pickupShipment?: {
    id: string;
    type: string;
    status: string;
    slot: { startAt: string; endAt: string };
    assignedRider?: { name: string; phone?: string };
  };
  deliveryShipment?: {
    id: string;
    type: string;
    status: string;
    otp?: string;
    slot: { startAt: string; endAt: string };
    assignedRider?: { name: string; phone?: string };
  };
  createdAt: string;
}

interface TimelineEntry {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  actor?: { id: string; publicId: string; name: string; role: string } | null;
  meta?: Record<string, unknown> | null;
  createdAt: string;
}

const statusConfig: Record<string, { color: string; label: string }> = {
  CREATED: { color: "bg-gray-100 text-gray-700", label: "Created" },
  PICKUP_SCHEDULED: { color: "bg-blue-100 text-blue-700", label: "Pickup Scheduled" },
  PICKED_UP: { color: "bg-teal-100 text-teal-700", label: "Picked Up" },
  RECEIVED_AT_WORKSHOP: { color: "bg-blue-100 text-blue-700", label: "Received" },
  INSPECTION_IN_PROGRESS: { color: "bg-sky-100 text-sky-700", label: "Inspecting" },
  QUOTE_SENT: { color: "bg-amber-100 text-amber-700", label: "Quote Sent" },
  AWAITING_PAYMENT: { color: "bg-yellow-100 text-yellow-700", label: "Awaiting Payment" },
  PAID: { color: "bg-green-100 text-green-700", label: "Paid" },
  IN_PRODUCTION: { color: "bg-blue-100 text-blue-700", label: "In Production" },
  QC: { color: "bg-sky-100 text-sky-700", label: "Quality Check" },
  READY_FOR_DELIVERY: { color: "bg-emerald-100 text-emerald-700", label: "Ready" },
  DELIVERY_ASSIGNED: { color: "bg-cyan-100 text-cyan-700", label: "Delivery Assigned" },
  OUT_FOR_DELIVERY: { color: "bg-orange-100 text-orange-700", label: "Out for Delivery" },
  DELIVERED: { color: "bg-green-100 text-green-700", label: "Delivered" },
  CANCELLED: { color: "bg-red-100 text-red-700", label: "Cancelled" },
};

export default function WorkshopOrderDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const orderId = params.id as string;

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<OrderData | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [revising, setRevising] = useState(false);
  const [showDispatch, setShowDispatch] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  async function fetchOrder() {
    setLoading(true);
    try {
      const response = await fetch(`/api/workshop/orders/${orderId}`);
      if (!response.ok) throw new Error("Failed to fetch order");

      const data = await response.json();
      setOrder(data.order);
      setTimeline(data.timeline || []);
    } catch (error) {
      toast.error("Failed to load order");
      navigate("/workshop/intake");
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveQuote(data: { items: { orderItemId: string; lines: any[] }[] }) {
    setSaving(true);
    try {
      const response = await fetch(`/api/workshop/orders/${orderId}/quote/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save quote");
      }

      toast.success("Quote saved as draft");
      fetchOrder();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save quote");
    } finally {
      setSaving(false);
    }
  }

  async function handleSendQuote() {
    if (!order?.activeQuote) return;

    setSending(true);
    try {
      const response = await fetch(`/api/workshop/quotes/${order.activeQuote.id}/send`, {
        method: "POST",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to send quote");
      }

      toast.success("Quote sent to customer");
      fetchOrder();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to send quote");
    } finally {
      setSending(false);
    }
  }

  async function handleReviseQuote() {
    if (!order?.activeQuote) return;

    setRevising(true);
    try {
      const response = await fetch(`/api/workshop/quotes/${order.activeQuote.id}/revise`, {
        method: "POST",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to revise quote");
      }

      toast.success("New draft created for revision");
      fetchOrder();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to revise quote");
    } finally {
      setRevising(false);
    }
  }

  async function handleAction(action: string) {
    setActionLoading(action);
    try {
      let url = "";
      let body: any = {};

      switch (action) {
        case "receive":
          url = `/api/workshop/orders/${orderId}/receive`;
          break;
        case "start_inspection":
          url = `/api/workshop/orders/${orderId}/inspection/start`;
          break;
        case "start_production":
          url = `/api/workshop/orders/${orderId}/production/start`;
          break;
        case "mark_qc":
          url = `/api/workshop/orders/${orderId}/qc/mark`;
          body = { to: "QC" };
          break;
        case "mark_ready":
          url = `/api/workshop/orders/${orderId}/qc/mark`;
          body = { to: "READY_FOR_DELIVERY" };
          break;
      }

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Action failed");
      }

      toast.success("Action completed");
      fetchOrder();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Action failed");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleCreateDelivery(slotId: string) {
    const response = await fetch(`/api/workshop/orders/${orderId}/dispatch/create-delivery`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deliverySlotId: slotId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create delivery");
    }

    toast.success("Delivery shipment created");
    fetchOrder();
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Order Details" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-[600px] lg:col-span-2" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  if (!order) return null;

  const status = statusConfig[order.status] || statusConfig.CREATED;
  const mapsUrl = order.address.lat && order.address.lng
    ? `https://www.google.com/maps?q=${order.address.lat},${order.address.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${order.address.line1}, ${order.address.city} ${order.address.pincode}`
      )}`;

  const canReceive = order.status === "PICKED_UP";
  const canStartInspection = order.status === "RECEIVED_AT_WORKSHOP";
  const canBuildQuote = ["RECEIVED_AT_WORKSHOP", "INSPECTION_IN_PROGRESS"].includes(order.status);
  const canStartProduction = order.status === "PAID";
  const canMarkQc = order.status === "IN_PRODUCTION";
  const canMarkReady = order.status === "QC";
  const canCreateDelivery = order.status === "READY_FOR_DELIVERY" && !order.deliveryShipment;
  const showQuoteBuilder = canBuildQuote && (!order.activeQuote || order.activeQuote.status === "DRAFT");

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Order ${order.publicId}`}
        actions={
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-muted-foreground" />
                  <CardTitle className="text-lg">{order.publicId}</CardTitle>
                  <Badge className={status.color}>{status.label}</Badge>
                  <Badge variant="outline">
                    {order.orderType === "ALTERATION" ? "Alteration" : "Stitching"}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">{order.customer.name}</p>
                    <p className="text-sm text-muted-foreground">{order.customer.publicId}</p>
                    {order.customer.email && (
                      <p className="text-sm text-muted-foreground">{order.customer.email}</p>
                    )}
                  </div>
                </div>
                {order.customer.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <span>{order.customer.phone}</span>
                  </div>
                )}
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium">{order.address.line1}</p>
                  {order.address.line2 && <p>{order.address.line2}</p>}
                  <p className="text-muted-foreground">
                    {order.address.city}, {order.address.state} - {order.address.pincode}
                  </p>
                </div>
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="h-4 w-4" />
                  Map
                </a>
              </div>

              {order.promisedDeliveryAt && (
                <>
                  <Separator />
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Promised Delivery</p>
                      <p className="font-medium">
                        {new Date(order.promisedDeliveryAt).toLocaleDateString()}
                      </p>
                    </div>
                    {order.cutoffEligible && (
                      <Badge className="bg-emerald-100 text-emerald-700">Fast Track</Badge>
                    )}
                  </div>
                </>
              )}

              <Separator />

              <div className="flex flex-wrap gap-2">
                {canReceive && (
                  <Button
                    onClick={() => handleAction("receive")}
                    disabled={actionLoading === "receive"}
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Receive at Workshop
                  </Button>
                )}
                {canStartInspection && (
                  <Button
                    onClick={() => handleAction("start_inspection")}
                    disabled={actionLoading === "start_inspection"}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Start Inspection
                  </Button>
                )}
                {canStartProduction && (
                  <Button
                    onClick={() => handleAction("start_production")}
                    disabled={actionLoading === "start_production"}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Start Production
                  </Button>
                )}
                {canMarkQc && (
                  <Button
                    onClick={() => handleAction("mark_qc")}
                    disabled={actionLoading === "mark_qc"}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark for QC
                  </Button>
                )}
                {canMarkReady && (
                  <Button
                    onClick={() => handleAction("mark_ready")}
                    disabled={actionLoading === "mark_ready"}
                  >
                    <Truck className="h-4 w-4 mr-2" />
                    Mark Ready for Delivery
                  </Button>
                )}
                {canCreateDelivery && (
                  <Button onClick={() => setShowDispatch(true)}>
                    <Truck className="h-4 w-4 mr-2" />
                    Create Delivery
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Shirt className="h-4 w-4" />
                Items ({order.items.reduce((sum, i) => sum + i.qty, 0)})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {order.items.map((item) => (
                  <div key={item.id} className="p-4 bg-muted/50 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{item.garmentType}</span>
                        <Badge variant="outline">x{item.qty}</Badge>
                        {item.tagCode && (
                          <Badge variant="secondary">Tag: {item.tagCode}</Badge>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {item.status.replace(/_/g, " ")}
                      </Badge>
                    </div>
                    {(item.conditionNotes || item.intakeNotes) && (
                      <p className="text-sm text-muted-foreground">
                        {item.intakeNotes || item.conditionNotes}
                      </p>
                    )}
                    {item.intakePhotos && (item.intakePhotos as string[]).length > 0 && (
                      <div className="flex gap-2 mt-2">
                        {(item.intakePhotos as string[]).slice(0, 4).map((photo, i) => (
                          <div
                            key={i}
                            className="w-16 h-16 rounded border bg-muted flex items-center justify-center overflow-hidden"
                          >
                            {photo.startsWith("data:") ? (
                              <img src={photo} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <img className="h-6 w-6 text-muted-foreground" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {order.measurements && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Ruler className="h-4 w-4" />
                  Measurements (v{order.measurements.versionNo})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MeasurementSilhouette
                  data={{
                    bust: order.measurements.bustCm,
                    waist: order.measurements.waistCm,
                    hip: order.measurements.hipCm,
                    shoulder: order.measurements.shoulderCm,
                    sleeveLength: order.measurements.sleeveLengthCm,
                    dressLength: order.measurements.dressLengthCm,
                    unit: "cm",
                  }}
                />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Quote
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue={showQuoteBuilder ? "builder" : "preview"}>
                <TabsList className="mb-4">
                  {showQuoteBuilder && <TabsTrigger value="builder">Build Quote</TabsTrigger>}
                  {order.activeQuote && <TabsTrigger value="preview">Current Quote</TabsTrigger>}
                  {order.quoteHistory.length > 0 && (
                    <TabsTrigger value="history">History ({order.quoteHistory.length})</TabsTrigger>
                  )}
                </TabsList>

                {showQuoteBuilder && (
                  <TabsContent value="builder">
                    <QuoteBuilder
                      items={order.items}
                      deliveryFee={order.area.deliveryFee}
                      onSave={handleSaveQuote}
                      saving={saving}
                    />
                  </TabsContent>
                )}

                {order.activeQuote && (
                  <TabsContent value="preview">
                    <QuotePreview
                      quote={order.activeQuote}
                      onSend={order.activeQuote.status === "DRAFT" ? handleSendQuote : undefined}
                      onRevise={
                        ["SENT", "VIEWED", "REJECTED", "EXPIRED"].includes(order.activeQuote.status)
                          ? handleReviseQuote
                          : undefined
                      }
                      sending={sending}
                      revising={revising}
                    />
                  </TabsContent>
                )}

                {order.quoteHistory.length > 0 && (
                  <TabsContent value="history">
                    <div className="space-y-2">
                      {order.quoteHistory.map((quote) => (
                        <div
                          key={quote.id}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded"
                        >
                          <div className="flex items-center gap-3">
                            <Badge variant="outline">v{quote.versionNo}</Badge>
                            <Badge className={statusConfig[quote.status]?.color || "bg-gray-100"}>
                              {quote.status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3">
                            <Money amount={quote.total} />
                            <span className="text-sm text-muted-foreground">
                              {new Date(quote.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                )}

                {!showQuoteBuilder && !order.activeQuote && order.quoteHistory.length === 0 && (
                  <p className="text-muted-foreground text-center py-8">
                    {canBuildQuote
                      ? "No quote created yet. Use the builder to create one."
                      : "Quote will be available after inspection."}
                  </p>
                )}
              </Tabs>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline entries={timeline} />
            </CardContent>
          </Card>

          {(order.pickupShipment || order.deliveryShipment) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Truck className="h-4 w-4" />
                  Shipments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {order.pickupShipment && (
                  <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Pickup</span>
                      <Badge variant="outline">{order.pickupShipment.status}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {new Date(order.pickupShipment.slot.startAt).toLocaleDateString()}
                    </p>
                    {order.pickupShipment.assignedRider && (
                      <p className="text-sm">
                        Rider: {order.pickupShipment.assignedRider.name}
                      </p>
                    )}
                  </div>
                )}

                {order.deliveryShipment && (
                  <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Delivery</span>
                      <Badge variant="outline">{order.deliveryShipment.status}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {new Date(order.deliveryShipment.slot.startAt).toLocaleDateString()}
                    </p>
                    {order.deliveryShipment.otp && (
                      <p className="text-sm">OTP: <span className="font-mono">{order.deliveryShipment.otp}</span></p>
                    )}
                    {order.deliveryShipment.assignedRider && (
                      <p className="text-sm">
                        Rider: {order.deliveryShipment.assignedRider.name}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <DispatchDialog
        open={showDispatch}
        onOpenChange={setShowDispatch}
        orderId={order.id}
        orderPublicId={order.publicId}
        areaId={order.area.id}
        onConfirm={handleCreateDelivery}
      />
    </div>
  );
}
